import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { route } from "../globals/constants";
import Loader from "../globals/elements/loader";
import InnerPageLayout from "../layouts/inner-page-layout";
import AdminLayout from "../layouts/admin-layout";
import ProtectedRoute from "../components/ProtectedRoute";

// Import home page directly (no lazy loading) - it's the most visited page
// This prevents remounting and image reloading when navigating back
import Home1Page from "../app/components/home/home1";

// Lazy load other pages for code splitting
const Home2Page = lazy(() => import("../app/components/home/home2"));
const Home3Page = lazy(() => import("../app/components/home/home3"));

const AboutUsPage = lazy(() => import("../app/components/pages/about-us"));
const FAQPage = lazy(() => import("../app/components/pages/faq"));
const TeamPage = lazy(() => import("../app/components/pages/team/team"));
const TeamDetailPage = lazy(() => import("../app/components/pages/team/detail"));
const ContactUs1Page = lazy(() => import("../app/components/pages/contact/contact-us1"));
const ContactUs2Page = lazy(() => import("../app/components/pages/contact/contact-us2"));
const GetQuotePage = lazy(() => import("../app/components/pages/quote/get-quote"));
const CustomerLoginPage = lazy(() => import("../app/components/pages/customer/customer-login"));
const Gallery2Page = lazy(() => import("../app/components/pages/gallery/gallery2"));
// FeaturesPage reserved for future use
const FeaturePage = lazy(() => import("../app/components/pages/features/feature"));
const Error404 = lazy(() => import("../app/components/pages/error404"));

const ServicesPage = lazy(() => import("../app/components/services/services"));
const ServiceDetailPage = lazy(() => import("../app/components/services/detail"));

const BlogListPage = lazy(() => import("../app/components/blog/list"));
const BlogListSidebarPage = lazy(() => import("../app/components/blog/list-sidebar"));
const BlogGridPage = lazy(() => import("../app/components/blog/grid"));
const BlogGridSidebarPage = lazy(() => import("../app/components/blog/grid-sidebar"));
const BlogPostSinglePage = lazy(() => import("../app/components/blog/post-single"));
const BlogDetailPage = lazy(() => import("../app/components/blog/detail"));

const ShopProductsPage = lazy(() => import("../app/components/shop/products"));
// ShopProductDetailPage reserved for future use (currently commented out in routes)
const ShopCartPage = lazy(() => import("../app/components/shop/cart"));
const ShopWishlistPage = lazy(() => import("../app/components/shop/wishlist"));
const ShopChekoutPage = lazy(() => import("../app/components/shop/checkout"));

const ElementsPage = lazy(() => import("../app/components/elements"));

const AdminLoginPage = lazy(() => import("../app/components/pages/admin/admin-login"));
const AdminMessages = lazy(() => import("../app/components/pages/admin/admin-messages"));
const AdminAddBlog = lazy(() => import("../app/components/pages/admin/admin-add-blog"));
const AdminEditBlog = lazy(() => import("../app/components/pages/admin/admin-edit-blog"));
const AdminGallery = lazy(() => import("../app/components/pages/admin/admin-gallery"));
const AdminGalleryList = lazy(() => import("../app/components/pages/admin/admin-gallery-list"));
const AdminBlogList = lazy(() => import("../app/components/pages/admin/admin-blog-list"));

function AppRoutes() {
    return (
        <Suspense fallback={<Loader />}>
            <Routes>
                <Route path={route.INITIAL} element={<Home1Page />}/>
                <Route path={route.home.HOME1} element={<Home1Page />}/>
                <Route path={route.home.HOME2} element={<Home2Page />}/>
                <Route path={route.home.HOME3} element={<Home3Page />}/>
                <Route path={route.pages.ABOUT}element={<InnerPageLayout content={<AboutUsPage />} />}/>
                <Route path={route.pages.FAQ} element={<InnerPageLayout content={<FAQPage />} />}/>
                <Route path={route.pages.team.TEAM} element={<InnerPageLayout content={<TeamPage />} />}/>
                <Route path={route.pages.team.DETAIL} element={<InnerPageLayout content={<TeamDetailPage />} />}/>
                <Route path={route.pages.contact.CONTACT1} element={<InnerPageLayout content={<ContactUs1Page />} />}/>
                <Route path={route.pages.contact.CONTACT2} element={<InnerPageLayout content={<ContactUs2Page />} />}/>
                <Route path={route.pages.contact.GET_QUOTE} element={<InnerPageLayout content={<GetQuotePage />} />}/>
                {/* Customer login should not use inner page banner/layout */}
                <Route path={route.customer.LOGIN} element={<CustomerLoginPage />}/>
                <Route path={route.pages.gallery.GALLERY1} element={<InnerPageLayout content={<Gallery2Page />} />}/>

                <Route path={route.pages.FEATURE} element={<InnerPageLayout content={<FeaturePage />} />}/>
                <Route path={route.services.SERVICES} element={<InnerPageLayout content={<ServicesPage />} />}/>
                <Route path={route.services.DETAIL} element={<InnerPageLayout content={<ServiceDetailPage />} />}/>
                <Route path={route.blog.LIST} element={<InnerPageLayout content={<BlogListPage />} />}/>
                <Route path={route.blog.LIST_SIDEBAR} element={<InnerPageLayout content={<BlogListSidebarPage />} />}/>
                <Route path={route.blog.GRID} element={<InnerPageLayout content={<BlogGridPage />} />}/>
                <Route path={route.blog.GRID_SIDEBAR} element={<InnerPageLayout content={<BlogGridSidebarPage />} />}/>
                <Route path={route.blog.POST_SINGLE} element={<InnerPageLayout content={<BlogPostSinglePage />} />}/>
                <Route path={route.blog.DETAIL} element={<InnerPageLayout content={<BlogDetailPage />} />}/>
                <Route path={route.shop.PRODUCTS} element={<InnerPageLayout content={<ShopProductsPage />} />}/>
                {/* <Route path={route.shop.DETAIL} element={<InnerPageLayout content={<ShopProductDetailPage />} />}/> */}
                <Route path={route.shop.CART} element={<InnerPageLayout content={<ShopCartPage />} />}/>
                <Route path={route.shop.WISHLIST} element={<InnerPageLayout content={<ShopWishlistPage />} />}/>
                <Route path={route.shop.CHECKOUT} element={<InnerPageLayout content={<ShopChekoutPage />} />}/>
                <Route path={route.ELEMENTS} element={<InnerPageLayout content={<ElementsPage />} />}/>
                
                {/* Admin Routes */}
                <Route path={route.admin.LOGIN} element={<AdminLoginPage />} />
                <Route 
                    path={route.admin.MESSAGES} 
                    element={
                        <ProtectedRoute>
                            <AdminLayout>
                                <AdminMessages />
                            </AdminLayout>
                        </ProtectedRoute>
                    } 
                />
                <Route
                    path={route.admin.BLOG_LIST}
                    element={
                        <ProtectedRoute>
                            <AdminLayout>
                                <AdminBlogList />
                            </AdminLayout>
                        </ProtectedRoute>
                    }
                />
                <Route 
                    path={route.admin.BLOG_CREATE}
                    element={
                        <ProtectedRoute>
                            <AdminLayout>
                                <AdminAddBlog />
                            </AdminLayout>
                        </ProtectedRoute>
                    }
                />
                <Route 
                    path={route.admin.BLOG_EDIT}
                    element={
                        <ProtectedRoute>
                            <AdminLayout>
                                <AdminEditBlog />
                            </AdminLayout>
                        </ProtectedRoute>
                    }
                />
                <Route 
                    path={route.admin.GALLERY_LIST}
                    element={
                        <ProtectedRoute>
                            <AdminLayout>
                                <AdminGalleryList />
                            </AdminLayout>
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path={route.admin.GALLERY_MANAGE}
                    element={
                        <ProtectedRoute>
                            <AdminLayout>
                                <AdminGallery />
                            </AdminLayout>
                        </ProtectedRoute>
                    } 
                />
                
                <Route path="*" element={<InnerPageLayout content={<Error404 />} />}/>
            </Routes>
        </Suspense>
    )
}
export default AppRoutes;